import React, { useState, useEffect, forwardRef } from "react";
import { cn } from "@/utils";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlurValidate?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Компонент ввода телефона.
 * Формат: +7 (XXX) XXX-XX-XX
 */
const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, onBlurValidate, placeholder = "+7 (000) 000-00-00", className, disabled = false }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      setDisplayValue(value || "");
    }, [value]);

    const formatPhone = (input: string, prevValue: string): string => {
      // Убираем всё кроме цифр и +
      let cleaned = input.replace(/[^\d+]/g, "");

      // Если пользователь удаляет символы, не форматируем
      if (cleaned.length < prevValue.replace(/[^\d+]/g, "").length) {
        // Возвращаем частично отформатированное значение
        return formatPartial(cleaned);
      }

      // Нормализуем начало номера
      if (cleaned.startsWith("+7")) {
        cleaned = cleaned.slice(2);
      } else if (cleaned.startsWith("8")) {
        cleaned = cleaned.slice(1);
      } else if (cleaned.startsWith("7") && cleaned.length > 1) {
        cleaned = cleaned.slice(1);
      }

      // Оставляем только 10 цифр после кода страны
      const digits = cleaned.replace(/\D/g, "").slice(0, 10);

      return formatPartial("+7" + digits);
    };

    const formatPartial = (input: string): string => {
      // Убираем всё кроме цифр и +
      let cleaned = input.replace(/[^\d+]/g, "");

      // Нормализуем +7
      if (cleaned.startsWith("+7")) {
        cleaned = "+7" + cleaned.slice(2);
      } else if (cleaned.startsWith("+")) {
        cleaned = "+7" + cleaned.slice(1);
      } else if (cleaned.startsWith("7") || cleaned.startsWith("8")) {
        cleaned = "+7" + cleaned.slice(1);
      } else if (cleaned.length > 0) {
        cleaned = "+7" + cleaned;
      }

      const digits = cleaned.slice(2); // Убираем +7

      if (digits.length === 0) {
        return cleaned.startsWith("+") ? "+7" : "";
      }

      let formatted = "+7";

      // (XXX)
      if (digits.length > 0) {
        formatted += " (" + digits.slice(0, 3);
        if (digits.length >= 3) {
          formatted += ")";
        }
      }

      // XXX
      if (digits.length > 3) {
        formatted += " " + digits.slice(3, 6);
      }

      // XX
      if (digits.length > 6) {
        formatted += "-" + digits.slice(6, 8);
      }

      // XX
      if (digits.length > 8) {
        formatted += "-" + digits.slice(8, 10);
      }

      return formatted;
    };

    const validatePhone = (phoneStr: string): string | null => {
      const digits = phoneStr.replace(/\D/g, "");

      if (digits.length < 11) {
        return null;
      }

      if (digits.length !== 11) {
        return "Телефон должен содержать 11 цифр";
      }

      if (!digits.startsWith("7")) {
        return "Телефон должен начинаться с +7";
      }

      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatPhone(input, displayValue);

      setDisplayValue(formatted);
      onChange(formatted);

      // Валидация полного номера (18 символов: +7 (XXX) XXX-XX-XX)
      if (formatted.length === 18) {
        const validationError = validatePhone(formatted);
        setError(validationError);
      } else {
        setError(null);
      }
    };

    const handleBlur = () => {
      if (displayValue && displayValue.length > 0 && displayValue.length < 18) {
        setError("Введите полный номер телефона");
      } else if (displayValue.length === 18 && onBlurValidate) {
        onBlurValidate(displayValue);
      }
    };

    const handleFocus = () => {
      // Если поле пустое, добавляем +7
      if (!displayValue) {
        setDisplayValue("+7");
        onChange("+7");
      }
    };

    return (
      <div className="w-full">
        <input
          ref={ref}
          type="tel"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={18}
          className={cn(
            "block w-full px-3 py-2 border rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
            "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/30 bg-red-50/50 dark:border-red-500 dark:bg-red-900/10"
              : "border-line",
            className,
          )}
        />
        {error && <p className="mt-1 text-xs text-error-600 dark:text-error-400">{error}</p>}
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
